import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="font-display text-2xl text-[#201815] mb-3">{title}</h2>
    <div className="text-sm text-[#5C4E48] leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function MentionsLegales() {
  useEffect(() => {
    document.title = "Mentions légales — Terre à Deux, café céramique à Auch";
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="mentions-legales-page" className="min-h-screen bg-[#FBF8F3]">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-16 md:py-24">
        <a href="/" data-testid="mentions-back-link" className="inline-flex items-center gap-2 text-sm text-[#8A7971] hover:text-[#C85A32] transition-colors mb-10">
          <ArrowLeft size={15} /> Retour au site
        </a>

        <p className="text-xs font-medium uppercase tracking-[0.25em] text-[#C85A32] mb-3">Informations légales</p>
        <h1 className="font-display tracking-tight text-[#201815] text-3xl sm:text-4xl lg:text-5xl mb-12">Mentions légales</h1>

        <Section title="1. Éditeur du site">
          <p>
            Le site est édité par <strong>Terre à Deux</strong>, café céramique, SARL au capital de 1 000 €.
          </p>
          <p>
            Siège social : 25 rue Dessoles, 32000 Auch, France.<br />
            Téléphone : 06 31 82 89 64<br />
            Email : terreadeux@gmail.com<br />
            SIREN : 130 595 895 — RCS Auch<br />
            TVA intracommunautaire : FR130595895
          </p>
          <p>Directeur de la publication : Stéphane Husson.</p>
        </Section>

        <Section title="2. Hébergement">
          <p>
            Le site est hébergé par la plateforme Emergent (emergent.sh). Les données sont hébergées au sein de l'infrastructure cloud de l'hébergeur.
          </p>
        </Section>

        <Section title="3. Propriété intellectuelle">
          <p>
            L'ensemble des contenus du site (logo, textes, photographies, illustrations) est la propriété exclusive de Terre à Deux, sauf mention contraire. Toute reproduction, représentation ou diffusion, totale ou partielle, sans autorisation écrite préalable est interdite.
          </p>
        </Section>

        <Section title="4. Données personnelles (RGPD)">
          <p>
            Dans le cadre des réservations d'ateliers, des commandes de cartes cadeaux et abonnements, et du programme de fidélité / parrainage, nous collectons les données suivantes : nom, prénom, adresse email, numéro de téléphone, ainsi que l'historique de vos réservations.
          </p>
          <p>
            Ces données sont utilisées uniquement pour : la gestion de vos réservations et commandes, l'envoi d'emails de confirmation et de rappel, le suivi de votre carte fidélité et des remises de parrainage. Elles ne sont ni vendues, ni cédées à des tiers.
          </p>
          <p>
            Elles sont conservées pendant la durée de la relation commerciale, puis archivées conformément aux obligations légales. Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés, vous disposez d'un droit d'accès, de rectification, d'opposition et de suppression de vos données. Pour l'exercer, écrivez à terreadeux@gmail.com.
          </p>
        </Section>

        <Section title="5. Cookies">
          <p>
            Le site utilise des cookies strictement nécessaires à son fonctionnement ainsi qu'un outil de mesure d'audience anonymisé. Aucun cookie publicitaire n'est déposé. Vous pouvez configurer votre navigateur pour refuser les cookies ; certaines fonctionnalités du site pourraient alors être limitées.
          </p>
        </Section>

        <Section title="6. Réservations en ligne">
          <p>
            Les réservations d'ateliers effectuées sur le site font l'objet d'une confirmation par email. Le règlement s'effectue sur place au café, le jour de l'atelier. En cas d'empêchement, merci de nous prévenir au 06 31 82 89 64 ou à terreadeux@gmail.com afin de libérer les places.
          </p>
        </Section>

        <Section title="7. Conditions générales d'utilisation du site (CGU)">
          <p>
            L'accès et l'utilisation du site impliquent l'acceptation pleine et entière des présentes conditions. Le site permet la présentation de l'activité du café céramique Terre à Deux, la réservation en ligne gratuite de créneaux d'ateliers (dans la limite de 14 places par créneau), ainsi que la commande de cartes cadeaux et d'abonnements avec règlement sur place.
          </p>
          <p>
            L'utilisateur s'engage à fournir des informations exactes lors de toute réservation ou commande. Toute réservation effectuée avec des coordonnées manifestement erronées pourra être annulée sans préavis.
          </p>
          <p>
            Terre à Deux s'efforce d'assurer l'exactitude des informations et la disponibilité du site, sans pouvoir le garantir de manière permanente (maintenance, panne, cas de force majeure). La responsabilité de Terre à Deux ne saurait être engagée en cas d'indisponibilité temporaire du site ou d'erreur dans les informations affichées.
          </p>
        </Section>

        <Section title="8. Conditions générales de vente (CGV)">
          <p>
            <strong>Prix.</strong> Les prix sont exprimés en euros, toutes taxes comprises (TTC). Le prix de l'atelier varie en fonction de la pièce choisie ; chaque pièce affichée en atelier comprend l'encadrement, les peintures, les outils, l'émaillage et la cuisson.
          </p>
          <p>
            <strong>Réservation et paiement.</strong> La réservation en ligne est gratuite et vaut mise de côté des places ; elle est confirmée par email avec un code de réservation. Le règlement s'effectue intégralement sur place, le jour de l'atelier, par carte bancaire ou en espèces. Toute place non honorée sans annulation préalable pourra être facturée dans le cadre des privatisations et réservations de groupe.
          </p>
          <p>
            <strong>Annulation.</strong> L'annulation est gratuite jusqu'à 24 heures avant le début de l'atelier, par téléphone (06 31 82 89 64) ou par email (terreadeux@gmail.com). Passé ce délai, les places seront remises en vente sans indemnité pour les créneaux individuels.
          </p>
          <p>
            <strong>Droit de rétractation.</strong> Conformément à l'article L221-28 12° du Code de la consommation, les prestations d'activités de loisirs fournies à une date ou à une période déterminée ne sont pas soumises au droit de rétractation de 14 jours.
          </p>
          <p>
            <strong>Cartes cadeaux.</strong> Les cartes cadeaux sont valables 1 an à compter de leur date d'achat, utilisables en une ou plusieurs fois sur les ateliers et la carte du café. Elles ne sont ni remboursables, ni échangeables, ni remplacées en cas de perte.
          </p>
          <p>
            <strong>Abonnements.</strong> Les abonnements (3 ou 5 ateliers) sont nominatifs et non remboursables ; leur durée de validité est précisée au moment de l'achat sur place.
          </p>
          <p>
            <strong>Fidélité et parrainage.</strong> La carte fidélité (1 atelier offert d'une valeur de 20€ après 5 ateliers cumulés) et les remises de parrainage (5€ pour le parrain et le filleul) sont appliquées sur place, au café. Elles ne sont pas cumulables entre elles sur une même prestation et n'ont pas de valeur monétaire.
          </p>
          <p>
            <strong>Retrait des pièces.</strong> Les pièces émaillées et cuites sont récupérables au café sous 2 semaines. Elles sont conservées pendant 2 mois à compter de la notification de disponibilité ; passé ce délai, elles ne pourront être réclamées.
          </p>
          <p>
            <strong>Litiges.</strong> Les présentes conditions sont soumises au droit français. En cas de litige, une solution amiable sera recherchée en priorité ; à défaut, le consommateur peut saisir gratuitement le médiateur de la consommation : SMP — Société Médiation Professionnelle (www.mediateur-consommation-smp.fr). À défaut de médiation, les tribunaux du ressort d'Auch seront compétents.
          </p>
        </Section>

        <Section title="9. Crédits">
          <p>Photographies : Terre à Deux. Certains visuels d'illustration : Pexels / Unsplash (licence libre d'utilisation).</p>
        </Section>

        <p className="text-xs text-[#8A7971] border-t border-[#E5DCD3] pt-6 mt-12">
          Dernière mise à jour : octobre 2026.
        </p>
      </div>
    </div>
  );
}
